import { GridColDef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { RewardingOpenWorkflowsData } from '~/data/reports';
import { getFormattedEmploymentValue } from '~/scenes/PeopleModule/components/Tabs/BasicTab/components/EmploymentDataForm/data';
import { convertToDateOrUndefined } from '~/utils/helper';

import { useReportNavigateToView } from '../../common';
import {
  RewardingOpenWorkflowsRow,
  WorkflowReportHookOutput,
  WorkflowReportHookProps,
} from '../../People/WorkflowReport/types';

export const useRewardingOpenWorkflowReport = (
  props: WorkflowReportHookProps<RewardingOpenWorkflowsData>,
): WorkflowReportHookOutput<RewardingOpenWorkflowsRow> => {
  const { data } = props;
  const { t } = useTranslation();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const { getEmployeeById } = useAllEmployees();

  const rows = useMemo<RewardingOpenWorkflowsRow[]>(() => {
    return data.flatMap((dataEntry) => {
      const {
        fEmpEmployeeNumber,
        fEmpFirstName,
        fEmpId,
        fEmpLastName,
        fEmpUnit,
        fEmpUnitNumber,
      } = dataEntry;

      return dataEntry.fEmpOpenWorkflow.map(
        (workflow): RewardingOpenWorkflowsRow => {
          const {
            fWorkflowAmount,
            fWorkflowLastUpdated,
            fWorkflowScheduledDate,
            fWorkflowStatus,
            fWorkflowType,
          } = workflow;

          const employee = getEmployeeById(fEmpId);

          return {
            employeeId: fEmpId,
            firstName: fEmpFirstName,
            lastName: fEmpLastName,
            employeeNumber: fEmpEmployeeNumber,
            position: employee?.fEmpPosition || '',
            organizationUnit: fEmpUnit,
            organizationNumber: fEmpUnitNumber,
            type: getFormattedEmploymentValue(fWorkflowType, '_', ' '),
            amount: fWorkflowAmount,
            status: getFormattedEmploymentValue(fWorkflowStatus),
            updated: convertToDateOrUndefined(fWorkflowLastUpdated),
            scheduledDate: convertToDateOrUndefined(fWorkflowScheduledDate),
          };
        },
      );
    });
  }, [data, getEmployeeById]);

  const columns = useMemo((): GridColDef<RewardingOpenWorkflowsRow>[] => {
    return [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
        minWidth: 130,
      },
      {
        field: 'position',
        headerName: t('laPosition'),
        minWidth: 150,
      },
      {
        field: 'organizationUnit',
        headerName: t('laOrgUnit'),
        minWidth: 150,
      },
      {
        field: 'organizationNumber',
        headerName: t('laUnitNumber'),
        minWidth: 150,
      },
      {
        field: 'type',
        headerName: t('laType'),
        minWidth: 150,
      },
      {
        field: 'amount',
        headerName: t('laAmount'),
        minWidth: 150,
      },
      {
        field: 'status',
        headerName: t('laStatus'),
        minWidth: 150,
      },
      {
        field: 'updated',
        headerName: t('Updated'),
        minWidth: 150,
        valueFormatter: (value) => moment(value).format('DD/MM/YYYY HH:mm:ss'),
      },
      {
        field: 'scheduledDate',
        headerName: t('laScheduledDate'),
        minWidth: 150,
        valueFormatter: dateCellValueFormatter,
      },
    ];
  }, [t, goToEmployeeBasicDataView]);

  return { rows, columns };
};
